import { motion } from 'framer-motion';
import { ShieldAlert, ArrowLeft, Lock, Landmark, Smartphone } from 'lucide-react';
import { Button } from '@/onboarding-demos-src/components/ui/button';
import { useNavigate } from 'react-router-dom';

interface AccessDeniedProps {
  requiredPermission?: string;
  adminOnly?: boolean;
}

export function AccessDenied({ requiredPermission, adminOnly = false }: AccessDeniedProps) {
  const navigate = useNavigate();

  const getModuleInfo = (slug?: string) => {
    const modules: Record<string, { name: string; icon: any; color: string }> = {
      'bbps-mobile': { name: 'BBPS Mobile', icon: Smartphone, color: 'emerald' },
      'merchant-onboarding': { name: 'Merchant Onboarding', icon: Smartphone, color: 'blue' },
      'cbs-mobile': { name: 'CBS Mobile Application', icon: Smartphone, color: 'blue' },
      'los-lms': { name: 'LOS, LMS & Digital Lending', icon: Landmark, color: 'blue' },
      'banking-app': { name: 'Banking App', icon: Landmark, color: 'indigo' },
      'bank-demo': { name: 'Bank Onboarding', icon: Landmark, color: 'indigo' },
      'cbs-demo': { name: 'CBS Onboarding', icon: ShieldAlert, color: 'cyan' },
    };
    return slug ? modules[slug] : null;
  };

  const moduleInfo = getModuleInfo(requiredPermission);

  return (
    <div className="w-full flex flex-col items-center justify-center p-6 lg:p-12 min-h-[70vh] font-sans selection:bg-rose-100">
      <div className="w-full max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white border border-slate-200 rounded-lg overflow-hidden flex flex-col items-center justify-center py-20 px-6 text-center"
        >
          {/* Header */}
          <div className="mb-10 flex flex-col items-center">
            <div className="w-20 h-20 bg-rose-50 rounded-2xl flex items-center justify-center text-rose-500 border border-rose-100 mb-8 relative">
              <ShieldAlert className="w-10 h-10" />
              <div className="absolute -top-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-sm border border-rose-50">
                <Lock className="w-3 h-3" />
              </div>
            </div>

            <h1 className="text-3xl font-black text-slate-900 tracking-tighter mb-4 uppercase">
              Access Restricted
            </h1>

            <p className="text-slate-500 font-semibold leading-relaxed max-w-lg">
              {adminOnly
                ? "This module is reserved for authorized system administrators. Your current clearance level is insufficient for this operation."
                : "Your session identity does not possess the mandatory authorization to enter this module. Contact administrator for privilege elevation."}
            </p>
          </div>

          {/* Module Insight (if applicable) */}
          {moduleInfo && !adminOnly && (
            <div className="w-full max-w-md p-6 bg-slate-50/50 rounded-lg border border-slate-100 flex items-center gap-5 translate-y-0 mb-10">
              <div className="w-14 h-14 rounded-xl bg-white border border-slate-100 flex items-center justify-center text-slate-800">
                <moduleInfo.icon className="w-6 h-6" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-0.5">Attempted Entry</p>
                <p className="text-base font-black text-slate-800 tracking-tight">{moduleInfo.name}</p>
              </div>
              <div className="px-3 py-1 bg-white text-rose-600 rounded-full text-[9px] font-black uppercase tracking-widest border border-rose-100 shadow-sm">
                Restricted
              </div>
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex flex-col items-center gap-8 w-full max-w-sm">
            <Button
              onClick={() => navigate('/')}
              className="w-full h-14 bg-slate-900 hover:bg-slate-800 text-white font-black text-[11px] uppercase tracking-[0.2em] rounded-lg flex items-center justify-center gap-3 transition-all active:scale-[0.98]"
            >
              <ArrowLeft className="w-4 h-4" /> Return to Home
            </Button>

            <div className="flex items-center justify-center gap-8 px-4 w-full">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
                <span className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">Sentinel Active</span>
              </div>
              <div className="w-px h-3 bg-slate-200" />
              <span className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em]">Error 403 • Forbidden</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
